import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';
import CallHistoryModal from './CallHistoryModal';

const { FiEdit2, FiTrash2, FiPhone, FiMail, FiBuilding, FiUser, FiSearch, FiClock, FiMessageSquare } = FiIcons;

const LeadsList = ({ leads, onUpdateLead, onDeleteLead, onSearchLeads }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLead, setEditingLead] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);

  const filteredLeads = leads.filter(lead =>
    lead.phone?.includes(searchTerm) ||
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (lead) => {
    setEditingLead(lead.id);
    setEditFormData(lead);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await onUpdateLead(editingLead, editFormData);
    setEditingLead(null);
    setEditFormData({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCallHistoryClick = (lead) => {
    setSelectedLead(lead);
  };

  const formatPhone = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    
    // Format as: 0744 155 921
    if (cleaned.length >= 1) {
      let formatted = '';
      
      // Add first digit (0)
      if (cleaned.length >= 1) {
        formatted += cleaned.substring(0, 1);
      }
      
      // Add first group (3 digits after 0)
      if (cleaned.length >= 4) {
        formatted += cleaned.substring(1, 4);
      } else if (cleaned.length > 1) {
        formatted += cleaned.substring(1);
        return formatted;
      }
      
      // Add space and second group (3 digits)
      if (cleaned.length >= 7) {
        formatted += ' ' + cleaned.substring(4, 7);
      } else if (cleaned.length > 4) {
        formatted += ' ' + cleaned.substring(4);
        return formatted;
      }
      
      // Add space and third group (3 digits)
      if (cleaned.length >= 10) {
        formatted += ' ' + cleaned.substring(7, 10);
      } else if (cleaned.length > 7) {
        formatted += ' ' + cleaned.substring(7);
        return formatted;
      }
      
      // Add remaining digits if any
      if (cleaned.length > 10) {
        formatted += ' ' + cleaned.substring(10);
      }
      
      return formatted;
    }
    
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setEditFormData(prev => ({ ...prev, phone: formatted }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'converted': return 'bg-green-100 text-green-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'qualified': return 'bg-blue-100 text-blue-800';
      case 'lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLastCallInfo = (lead) => {
    if (!lead.callHistory || lead.callHistory.length === 0) return null;
    
    const lastCall = lead.callHistory
      .sort((a, b) => b.timestamp - a.timestamp)[0];
    
    return lastCall;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Leads Database</h2>
        <div className="text-sm text-gray-500">
          {filteredLeads.length} of {leads.length} leads
        </div>
      </div>

      <div className="mb-6 relative">
        <SafeIcon
          icon={FiSearch}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search leads by name, phone, email, or company..."
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {filteredLeads.length === 0 ? (
        <div className="text-center py-12">
          <SafeIcon icon={FiUser} className="text-gray-400 text-4xl mb-4 mx-auto" />
          <p className="text-gray-500">No leads found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead) => {
            const lastCall = getLastCallInfo(lead);
            
            return (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                {editingLead === lead.id ? (
                  <form onSubmit={handleEditSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        name="name"
                        value={editFormData.name || ''}
                        onChange={handleEditChange}
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Name"
                      />
                      <input
                        type="tel"
                        name="phone"
                        value={editFormData.phone || ''}
                        onChange={handlePhoneChange}
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0744 155 921"
                      />
                      <input
                        type="email"
                        name="email"
                        value={editFormData.email || ''}
                        onChange={handleEditChange}
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Email"
                      />
                      <input
                        type="text"
                        name="company"
                        value={editFormData.company || ''}
                        onChange={handleEditChange}
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Company"
                      />
                      <select
                        name="status"
                        value={editFormData.status || 'new'}
                        onChange={handleEditChange}
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="converted">Converted</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                    <textarea
                      name="notes"
                      value={editFormData.notes || ''}
                      onChange={handleEditChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Notes"
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingLead(null)}
                        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <SafeIcon icon={FiUser} className="text-gray-400" />
                          <span className="font-semibold">{lead.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <SafeIcon icon={FiPhone} className="text-gray-400" />
                          <span>{lead.phone}</span>
                        </div>
                      </div>
                      <div>
                        {lead.email && (
                          <div className="flex items-center gap-2 mb-1 text-sm text-gray-600">
                            <SafeIcon icon={FiMail} className="text-gray-400" />
                            <span>{lead.email}</span>
                          </div>
                        )}
                        {lead.company && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <SafeIcon icon={FiBuilding} className="text-gray-400" />
                            <span>{lead.company}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded-full text-xs w-fit ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                        {lead.createdAt && (
                          <span className="text-xs text-gray-500">
                            Added: {format(lead.createdAt.toDate(), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        {lastCall ? (
                          <div className="text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <SafeIcon icon={FiClock} className="text-gray-400" />
                              <span>Last call: {format(new Date(lastCall.timestamp), 'MMM dd')}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {lastCall.outcome.replace('-', ' ')}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No calls yet</span>
                        )}
                        <div className="text-xs">
                          <span className="text-blue-600">
                            {lead.callHistory?.length || 0} calls
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleCallHistoryClick(lead)}
                        className="p-2 text-green-500 hover:bg-green-50 rounded"
                        title="Call History"
                      >
                        <SafeIcon icon={FiPhone} />
                      </button>
                      <button
                        onClick={() => handleEditClick(lead)}
                        className="p-2 text-blue-500 hover:bg-blue-50 rounded"
                        title="Edit Lead"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button
                        onClick={() => onDeleteLead(lead.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                        title="Delete Lead"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </div>
                )}
                {lead.notes && editingLead !== lead.id && (
                  <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                    <strong>Notes:</strong> {lead.notes}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Call History Modal */}
      {selectedLead && (
        <CallHistoryModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdateLead={onUpdateLead}
        />
      )}
    </div>
  );
};

export default LeadsList;